import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { CheckCircle2 } from "lucide-react";

/**
 * Centered success popup with 360° rainbow confetti burst.
 *
 * Triggers when `name` becomes a non-empty string. Auto-dismisses after
 * 4 seconds. Renders a fixed-position card with the production name +
 * "הוקמה בהצלחה!" message. Confetti pieces fire from the centre in all
 * directions using two opposite-angle bursts (a single fire() shows the
 * pieces on one arc; firing twice covers the full 360°).
 *
 * Why a popup instead of a toast? Toasts appear in a corner and disappear
 * quickly — the user explicitly asked for a centred modal-style success
 * confirmation with a celebration effect that says "this worked".
 */
export function ProductionCreatedPopup({ name, onClose }) {
  // Track the timer so a quick consecutive create-create doesn't pile up
  // overlapping auto-close handlers.
  const timerRef = useRef(null);

  useEffect(() => {
    if (!name) return;

    // Two opposite arcs = full 360° burst.
    const colors = ["#ec4899", "#a855f7", "#f59e0b", "#10b981", "#3b82f6", "#ef4444", "#eab308", "#06b6d4"];
    const burst = (originX) =>
      confetti({
        particleCount: 80,
        spread: 360,
        startVelocity: 35,
        ticks: 200,
        gravity: 0.7,
        scalar: 1.1,
        origin: { x: originX, y: 0.5 },
        colors,
        zIndex: 100000,
      });
    burst(0.5);
    // Slight stagger looks more organic than a single mega-burst.
    setTimeout(() => burst(0.5), 200);
    setTimeout(() => burst(0.5), 450);

    timerRef.current = setTimeout(() => {
      onClose?.();
    }, 4000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [name, onClose]);

  if (!name) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      data-testid="ff-production-created-popup"
    >
      <div className="pointer-events-auto bg-stone-900 border-2 border-pink-500/60 rounded-2xl shadow-2xl shadow-pink-500/30 px-8 py-6 max-w-md w-[92%] text-center animate-in fade-in zoom-in duration-300">
        <div className="flex flex-col items-center gap-3">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20 border-2 border-emerald-500/50">
            <CheckCircle2 className="h-8 w-8 text-emerald-300" />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white">
              הפקה ({name}) הוקמה
            </h2>
            <p className="text-lg font-bold text-emerald-300">בהצלחה!</p>
          </div>
        </div>
      </div>
    </div>
  );
}
